import React, { useState, useEffect, memo } from 'react';
import { Bar } from 'react-chartjs-2';
import { analyticsAPI } from '../../../services/analytics';
import ChartCard from './ChartCard';
import UserListModal from './UserListModal';

const RepeatRateChart = memo(() => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userModal, setUserModal] = useState({ isOpen: false, users: [], title: '' });

  useEffect(() => {
    analyticsAPI.getRepeatRate()
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleChartClick = (event, elements) => {
    if (!elements.length || !data) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const index = elements[0].index;
    const users = index === 0 ? data.repeat_customers_list || [] : data.new_customers_list || [];
    const title = index === 0 ? 'Repeat Customers' : 'New Customers';
    setTimeout(() => setUserModal({ isOpen: true, users, title }), 300);
  };

  if (loading) return (
    <div className="col-span-12 bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex items-center justify-center h-48">
      <div className="animate-spin rounded-full h-7 w-7 border-2 border-indigo-500 border-t-transparent" />
    </div>
  );

  if (!data) return null;

  const newCustomers = data.total_customers - data.repeat_customers;
  const repeatPct = ((data.repeat_customers / (data.total_customers || 1)) * 100).toFixed(1);

  const repeatChartData = {
    labels: ['Repeat', 'New'],
    datasets: [{
      data: [data.repeat_customers, newCustomers],
      backgroundColor: ['#10B981', '#6366F1'],
      borderWidth: 0,
      borderRadius: 6,
    }]
  };

  const statsChartData = {
    labels: ['Total Orders', 'Total Customers', 'Repeat Customers'],
    datasets: [{
      data: [data.total_orders_in_db, data.total_customers, data.repeat_customers],
      backgroundColor: ['#3B82F6', '#8B5CF6', '#10B981'],
      borderWidth: 0,
      borderRadius: 6,
    }]
  };

  const opts = (onClick) => ({
    responsive: true,
    maintainAspectRatio: false,
    onClick,
    plugins: { legend: { display: false } },
    scales: {
      y: { beginAtZero: true, grid: { display: false }, ticks: { font: { size: 10 } } },
      x: { grid: { display: false }, ticks: { font: { size: 10 } } }
    }
  });

  return (
    <>
      {/* Stat summary cards */}
      <div className="col-span-12 grid grid-cols-2 sm:grid-cols-4 gap-3 mb-1">
        {[
          { label: 'Total Customers', value: data.total_customers?.toLocaleString(), color: 'text-violet-600', bg: 'bg-violet-50', icon: '👥' },
          { label: 'Repeat Customers', value: data.repeat_customers?.toLocaleString(), color: 'text-emerald-600', bg: 'bg-emerald-50', icon: '🔁' },
          { label: 'Repeat Rate', value: `${repeatPct}%`, color: parseFloat(repeatPct) >= 30 ? 'text-emerald-600' : 'text-amber-600', bg: parseFloat(repeatPct) >= 30 ? 'bg-emerald-50' : 'bg-amber-50', icon: '📈' },
          { label: 'Avg Orders/Customer', value: data.average_orders_per_customer, color: 'text-blue-600', bg: 'bg-blue-50', icon: '📦' },
        ].map((s, i) => (
          <div key={i} className={`${s.bg} rounded-xl px-4 py-3 flex items-center gap-3 border border-white`}>
            <span className="text-xl">{s.icon}</span>
            <div>
              <p className="text-xs text-gray-500">{s.label}</p>
              <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <ChartCard
        title="Repeat vs New Customers"
        subtitle="Click bar to view customer list"
        className="col-span-12 lg:col-span-5"
        insight={`${repeatPct}% repeat rate`}
      >
        <div className="h-52">
          <Bar data={repeatChartData} options={opts(handleChartClick)} />
        </div>
      </ChartCard>

      <ChartCard
        title="Customer Statistics"
        subtitle={`Avg ${data.average_orders_per_customer} orders per customer`}
        className="col-span-12 lg:col-span-7"
        insight={`${data.total_orders_in_db?.toLocaleString()} total orders`}
      >
        <div className="h-52">
          <Bar data={statsChartData} options={opts(null)} />
        </div>
      </ChartCard>

      <UserListModal
        isOpen={userModal.isOpen}
        onClose={() => setUserModal({ isOpen: false, users: [], title: '' })}
        users={userModal.users}
        month={userModal.title}
        year=""
      />
    </>
  );
});

export default RepeatRateChart;
